import React, { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react';

import BaseDialog from 'core/components/base-dialog';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import { Loading } from 'core/components/loading';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import { OfferStatus } from 'core/store/offer-store';
import { track } from 'core/helpers/analytics';
import { trackProspectFunnelEvent } from 'core/helpers/prospect-funnel';
import { types } from 'core/helpers/sanitize';
import getQuoteFriendlySelectedOption from 'offer/helpers/quote-friendly-selected-option';
import alertIcon from 'core/assets/svg/alert-dark-green-bold.svg';
import copyIcon from 'core/assets/svg/copy-white.svg';
import downloadIcon from 'core/assets/svg/download-doc-white.svg';
import useStyles from './offer-sharing-options.styles';

const OfferSharingOptions = observer(({ open, onClose, copyLink, loading }) => {
  const classes = useStyles();
  const {
    offer: offerStore,
    affinityLookups,
    account: { sendEventAsMember }
  } = useStore();
  const { offer, downloadQuote, downloadOffer, status, selectedOption } = offerStore;
  const { clusterId, code, id: offerId, quote } = offer;
  const quoteFriendlyOption = getQuoteFriendlySelectedOption(selectedOption);
  const session = useSession();
  const { user, isAgency } = session;

  const affinity = quote?.global?.affinity;
  const affinityData = affinity && affinityLookups.data.find((aff) => aff.affinity.startsWith(affinity));
  const agencyAffinity = affinity && affinityData?.isAgency;
  const dontOverwriteRep = Boolean(!isAgency && agencyAffinity);

  useEffect(() => {
    if (open) {
      track('Offer sharing options - viewed', { offerId, isAgency });
    }
  }, [open, offerId, isAgency]);

  // Dual emit: keep the staff-identity events and add prospect-keyed funnel
  // copies via sendEventAsMember so the funnel can include a Sharing step.
  const handleCopyLockedLink = () => {
    track('Offer sharing options - Locked offer link copied', { offerId, isAgency });
    trackProspectFunnelEvent({
      sendEventAsMember,
      eventName: 'Staff Funnel: Locked Offer Link Copied',
      offer,
      session
    });
    const readOnly = true;
    copyLink(readOnly);
  };

  const handleCopyUnlockedLink = () => {
    track('Offer sharing options - Unlocked offer link copied', { offerId, isAgency });
    trackProspectFunnelEvent({
      sendEventAsMember,
      eventName: 'Staff Funnel: Unlocked Offer Link Copied',
      offer,
      session
    });
    const readOnly = false;
    copyLink(readOnly);
  };

  const handleDownloadOffer = useCallback(() => {
    track('Offer sharing options - Offer PDF downloaded', {
      offerId,
      isAgency,
      entryPoint: 'Offer sharing options modal'
    });
    trackProspectFunnelEvent({
      sendEventAsMember,
      eventName: 'Staff Funnel: Offer PDF Downloaded',
      offer,
      session,
      extraProps: { entryPoint: 'Offer sharing options modal' }
    });
    downloadOffer({
      offerId,
      clusterId,
      code,
      policyType: quoteFriendlyOption,
      bypassCache: false,
      revisedQuoteDetails: types.QuoteDetailsInput({
        ...quote,
        rep: quote?.rep?.length && dontOverwriteRep ? quote.rep : user.username
      })
    });
  }, [
    downloadOffer,
    offerId,
    isAgency,
    clusterId,
    code,
    quoteFriendlyOption,
    quote,
    dontOverwriteRep,
    user,
    sendEventAsMember,
    offer,
    session
  ]);

  const handleDownloadApplicationPdf = useCallback(() => {
    track('Offer sharing options - Application PDF downloaded', {
      offerId,
      isAgency,
      entryPoint: 'Offer sharing options modal'
    });
    trackProspectFunnelEvent({
      sendEventAsMember,
      eventName: 'Staff Funnel: Application PDF Downloaded',
      offer,
      session,
      extraProps: { entryPoint: 'Offer sharing options modal' }
    });
    downloadQuote({ bypassCache: false });
  }, [downloadQuote, offerId, isAgency, sendEventAsMember, offer, session]);

  return (
    <BaseDialog open={open} onClose={onClose} className={classes.modal} size="md">
      <div className={classes.container}>
        <Label type="greenBig" className={classes.title}>
          Offer sharing options
        </Label>

        <div className={classes.optionCards}>
          <div className={classes.optionCard}>
            <div className={classes.optionCardMain}>
              <div className={classes.optionText}>
                <Label className={classes.optionTitle}>Share a locked offer link</Label>
                <Label className={classes.optionDescription} variant="body2">
                  Allows the applicant to review and buy online, but they can&apos;t make changes.
                </Label>
              </div>
              <Button className={classes.actionButton} onClick={handleCopyLockedLink}>
                {loading ? (
                  <Loading type="secondary" noBackground size={20} />
                ) : (
                  <>
                    <img src={copyIcon} alt="" className={classes.buttonIcon} /> Copy locked link
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className={classes.optionCard}>
            <div className={classes.optionCardMain}>
              <div className={classes.optionText}>
                <Label className={classes.optionTitle}>Share an unlocked offer link</Label>
                <Label className={classes.optionDescription}>
                  Allows the applicant to review, edit the offer, and buy online (price may change).
                </Label>
              </div>
              <Button className={classes.actionButton} onClick={handleCopyUnlockedLink}>
                <img src={copyIcon} alt="" className={classes.buttonIcon} /> Copy unlocked link
              </Button>
            </div>
            <div className={classes.cardFooter}>
              <div className={classes.cardFooterContent}>
                <img src={alertIcon} alt="Alert icon" className={classes.alertIcon} />
                <Label variant="body3" className={classes.cardFooterText}>
                  Changes to key details (like drivers or vehicles) may require rerunning reports (e.g., MVR). This can
                  change the price and could add report fees in some states.
                </Label>
              </div>
            </div>
          </div>

          <div className={classes.optionCard}>
            <div className={classes.optionCardMain}>
              <div className={classes.optionText}>
                <Label className={classes.optionTitle}>Share a PDF of the quote</Label>
                <Label className={classes.optionDescription}>
                  Downloads a view-only PDF you can forward or print. It doesn&apos;t include a link to buy online.
                </Label>
              </div>
              <Button className={`${classes.actionButton} ${classes.downloadButton}`} onClick={handleDownloadOffer}>
                {status === OfferStatus.DownloadingOffer ? (
                  <>
                    <Loading type="secondary" noBackground size={20} /> Generating PDF...
                  </>
                ) : (
                  <>
                    <img src={downloadIcon} alt="" className={classes.buttonIcon} /> Download quote PDF
                  </>
                )}
              </Button>
            </div>
            <div className={classes.cardFooter}>
              <Label className={classes.cardFooterText} inlineDisplay>
                Need the full application and required forms?{' '}
                {status === OfferStatus.DownloadingQuote ? (
                  <>
                    <Loading type="secondary" noBackground size={14} />
                    <span className={classes.generatingPdfText}>Generating PDF...</span>
                  </>
                ) : (
                  <span
                    className={classes.footerLink}
                    onClick={handleDownloadApplicationPdf}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleDownloadApplicationPdf();
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    Download Application PDF
                  </span>
                )}
              </Label>
            </div>
          </div>
        </div>
      </div>
    </BaseDialog>
  );
});

export default OfferSharingOptions;
